import type { UserStatus } from "@/Common/Models/Domain/UserStatus";
import type { Break } from "@/Modules/Breaks/Models/Domain";
import type { LegalAgreement } from "@/Modules/Models/Domain/LegalAgreement";
import type { Subscription } from "@/Modules/Models/Domain/Subscription";
import type { User, Transcription } from "@/Modules/Models/Domain/User";

// this is very old: bender was the first "therapist" in Therapyside, this is the user that the app use
// at the beginning like Cloe. Right now, Bender still permanent in our hearts and in backend code.
export const BENDER = "bender";

export enum ChatUserStatus {
  OFFLINE = "OFFLINE",
  ONLINE = "ONLINE",
  COMPOSING = "COMPOSING",
}

export enum ChatBlockReason {
  CHANGE_DURATION_30_TO_50 = "change_duration_30_to_50",
  CHANGE_DURATION_50_TO_30 = "change_duration_50_to_30",
  CHANGE_DURATION_50_TO_30_ADVICE = "change_duration_50_to_30_advice",
}

export enum ChatRole {
  PATIENT = 0,
  THERAPIST = 1,
  COACH = 2,
}

export enum ChatRoomItemType {
  INCOMING_TEXT = "incomingText",
  OUTGOING_TEXT = "outgoingText",
  NEUTRAL_TEXT = "neutralText",
  NEUTRAL_TEXT_GREY = "neutralTextGrey",
  NEUTRAL_TIME_TEXT = "neutralTimeText",
  SUBSCRIPTION_REQUEST = "subscriptionRequest",
  SHOW_PLANS = "show_plans",
  UNKNOWN = "unknown",
  PATIENT_COME_BACK = "patient_come_back",
  PATIENT_SSO = "patient_sso",
  PATIENT_REASSIGN = "patient_reassign",
}

export interface ChatRoomItem {
  createdAt: Date;
  id: string;
  type: ChatRoomItemType;
  old: boolean;
  isReceived: boolean;
}

export interface TextChatRoomItem extends ChatRoomItem {
  text: string;
  type: ChatRoomItemType.INCOMING_TEXT | ChatRoomItemType.OUTGOING_TEXT;
  isConfirmed?: boolean; // Is the event added to the chat (backend confirmation via ws) only outgoing message
}

export interface ImageChatRoomItem extends ChatRoomItem {
  thumbnail: string;
  url: string;
  type: ChatRoomItemType.INCOMING_TEXT | ChatRoomItemType.OUTGOING_TEXT;
  isConfirmed?: boolean; // Is the event added to the chat (backend confirmation via ws) only outgoing message
  height: number;
  width: number;
}

export interface AudioChatRoomItem extends ChatRoomItem {
  type: ChatRoomItemType.INCOMING_TEXT | ChatRoomItemType.OUTGOING_TEXT;
  isConfirmed?: boolean; // Is the event added to the chat (backend confirmation via ws) only outgoing message
  length: number;
  size: number;
  url: string;
}

export interface DocumentChatRoomItem extends ChatRoomItem {
  fileName: string;
  url: string;
  size: number;
  type: ChatRoomItemType.INCOMING_TEXT | ChatRoomItemType.OUTGOING_TEXT;
  isConfirmed?: boolean; // Is the event added to the chat (backend confirmation via ws) only outgoing message
}

export interface CustomChatRoomItem extends ChatRoomItem {
  i18nKey?: string;
  text?: string;
  params?: Record<string, unknown>;
  type:
    | ChatRoomItemType.SUBSCRIPTION_REQUEST
    | ChatRoomItemType.NEUTRAL_TEXT
    | ChatRoomItemType.NEUTRAL_TEXT_GREY
    | ChatRoomItemType.NEUTRAL_TIME_TEXT
    | ChatRoomItemType.SHOW_PLANS
    | ChatRoomItemType.PATIENT_COME_BACK
    | ChatRoomItemType.PATIENT_SSO
    | ChatRoomItemType.PATIENT_REASSIGN
    | ChatRoomItemType.UNKNOWN; // Update textChatRoomItemTypes or customChatRoomItemTypes and ChatRoomItemTypeGuard if you add or remove types
}

export interface UnknownChatRoomItem extends Pick<ChatRoomItem, "id" | "type"> {
  type: ChatRoomItemType.UNKNOWN;
}

export const textChatRoomItemTypes = [ChatRoomItemType.INCOMING_TEXT, ChatRoomItemType.OUTGOING_TEXT];
export const customChatRoomItemTypes = [
  ChatRoomItemType.SUBSCRIPTION_REQUEST,
  ChatRoomItemType.NEUTRAL_TEXT,
  ChatRoomItemType.NEUTRAL_TEXT_GREY,
  ChatRoomItemType.NEUTRAL_TIME_TEXT,
  ChatRoomItemType.SHOW_PLANS,
  ChatRoomItemType.PATIENT_COME_BACK,
  ChatRoomItemType.PATIENT_SSO,
  ChatRoomItemType.PATIENT_REASSIGN,
];

export type AllowedChatRoomItemType =
  | TextChatRoomItem
  | CustomChatRoomItem
  | ImageChatRoomItem
  | DocumentChatRoomItem
  | AudioChatRoomItem;

export type AllChatRoomItemType = AllowedChatRoomItemType | UnknownChatRoomItem;

export interface ChatRoomUser extends Pick<User, "avatar" | "id" | "username"> {
  isB2b: boolean;
  isCouple: boolean;
  isUnder18: boolean;
  isPromo: boolean;
  chatStatus: null | UserStatus;
  status?: ChatUserStatus;
  lastConnection?: number;
  subscription?: Subscription;
  legalAgreements?: LegalAgreement[];
  isLoadingUser: boolean; // Boolean for user loading (toolbar)
  customerUuid?: string;
}

export interface ParticipantRoomItem {
  caps: number;
  ietag: string;
  image: string | null;
  isB2b: boolean;
  isCouple: boolean;
  isUnder18: boolean;
  isPromo: boolean;
  left: boolean;
  name: string;
  role: ChatRole;
  state: null | UserStatus;
  user: string;
}

export interface ChatRoom {
  id: string;
  events: AllChatRoomItemType[];
  unreadEvents: number;
  numberNotifications?: number;
  user: ChatRoomUser;
  lastEvent?: TextChatRoomItem;
  currentBreak?: Break;
  participants: ParticipantRoomItem[];
  isLoadingEvents: boolean;
  isAiEnabled: boolean;
  transcription: Transcription | null;
  freeSessions?: number | null;
  sessionDuration: number;
  blockReason?: ChatBlockReason | null;
}

export enum FileTypes {
  IMAGE = "image",
  DOCUMENT = "document",
  AUDIO = "AUDIO",
}
export interface SendFileQuery<T = File> {
  file: T;
  type: FileTypes;
}

export interface AwsUploadRequestResponse {
  acl: string;
  awsAccessKey: string;
  id: string;
  maxSize: number;
  path: string;
  policy: string;
  signature: string;
  url: string;
}
